import React, { ReactNode } from 'react'
import { X } from 'lucide-react'

interface CustomDialogProps {
 isOpen?: boolean
 open?: boolean
 onClose?: () => void
 onOpenChange?: (open: boolean) => void
 title?: string
 description?: string
 children?: ReactNode
 showCloseButton?: boolean
 size?: 'sm' | 'md' | 'lg' | 'xl'
}

interface DialogHeaderProps {
 title?: string
 description?: string
 onClose?: () => void
 showCloseButton?: boolean
}

interface DialogContentProps {
 children: ReactNode
}

interface DialogFooterProps {
 children: ReactNode
}

const CustomDialog: React.FC<CustomDialogProps> & {
 Header: React.FC<DialogHeaderProps>
 Content: React.FC<DialogContentProps>
 Footer: React.FC<DialogFooterProps>
} = ({
 isOpen,
 open,
 onClose,
 onOpenChange,
 title,
 description,
 children,
 showCloseButton = true,
 size = 'md'
}) => {
 const dialogOpen = isOpen ?? open ?? false
 const handleClose = onClose || (() => onOpenChange?.(false))

 if (!dialogOpen) return null

 const sizeClasses = {
 sm: 'max-w-md',
 md: 'max-w-lg',
 lg: 'max-w-2xl',
 xl: 'max-w-4xl'
 }

 return (
 <div className="fixed inset-0 z-50 overflow-y-auto">
 {/* Overlay */}
 <div 
 className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
 onClick={handleClose}
 />
 
 {/* Dialog */}
 <div className="flex min-h-full items-center justify-center p-4">
 <div className={`relative w-full ${sizeClasses[size]} bg-card rounded-lg shadow-xl transform transition-all`}>
 {/* Header */}
 <CustomDialog.Header
 title={title}
 description={description}
 onClose={handleClose}
 showCloseButton={showCloseButton}
 />
 
 {/* Content */}
 <div className="px-6 py-4">
 {children}
 </div>
 </div>
 </div>
 </div>
 )
}

const DialogHeader: React.FC<DialogHeaderProps> = ({
 title,
 description,
 onClose,
 showCloseButton = true
}) => (
 <div className="flex items-center justify-between p-6 border-b border-border">
 <div className="flex-1">
 <h3 className="text-lg font-semibold text-foreground">
 {title}
 </h3>
 {description && (
 <p className="mt-1 text-sm text-muted-foreground">
 {description}
 </p>
 )}
 </div>
 {showCloseButton && onClose && (
 <button
 type="button"
 onClick={onClose}
 className="ml-4 p-2 text-muted-foreground hover:text-muted-foreground dark:hover:text-gray-300 rounded-lg hover:bg-surface transition-colors"
 >
 <X className="h-5 w-5" />
 </button>
 )}
 </div>
)

const DialogContent: React.FC<DialogContentProps> = ({ children }) => (
 <div className="text-foreground">
 {children}
 </div>
)

const DialogFooter: React.FC<DialogFooterProps> = ({ children }) => (
 <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-border bg-surface/50 rounded-b-lg">
 {children}
 </div>
)

// Attach sub-components to main component
CustomDialog.Header = DialogHeader
CustomDialog.Content = DialogContent
CustomDialog.Footer = DialogFooter

export default CustomDialog

